import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useReactToPrint } from "react-to-print";
import "./report.css";

// Custom dropdown component for filtering
const CustomDropdown = ({ name, value, onChange, options }) => {
  const handleDropdownChange = (e) => {
    const selectedValue = parseInt(e.target.value);
    onChange({ target: { name, value: selectedValue } });
  };

  const defaultOptions = [
    { value: -1, label: "Search Record" },
    { value: 1, label: "Linked" },
    { value: 2, label: "Company" },
    { value: 3, label: "Invalid PAN" },
    { value: 0, label: "Unlinked" },
  ];

  const dropdownOptions = options || defaultOptions;

  return (
    <select
      name={name}
      value={value}
      className="form-control"
      onChange={handleDropdownChange}
    >
      {dropdownOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// Default column filter component
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ""}
      className="form-control"
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

const DraggableReportTable = React.forwardRef(
  (
    {
      columns: initialColumns,
      data,
      fetchData,
      dateObject,
      loading,
      pageCount: controlledPageCount,
      title = "",
      canView = false,
      canPreview = false,
      handlePreviewPod,
      handleDownloadPod,
      handleDeletePod,
      handleViewClick,
      checkbox = false,
      handleCheckboxChange,
      checkedList,
      isChecked,
      getSelectAllValue,
      handleSelectAll,
      handleDeselectAll,
      customFilters: externalCustomFilters = {},
      onColumnReorder,
      showTransitFilter = false,
      showExtendedTimesFilter = false,
      customActions = [],
      role_id = null,
      ...props
    },
    ref
  ) => {
    // Get session data
    const companyId =
      JSON.parse(sessionStorage.getItem("company_id"))?.company_id ?? "1";
    const fYear_fetch =
      JSON.parse(sessionStorage.getItem("financial_year_for_fetch"))
        ?.financial_year_for_fetch || "";

    // State management
    const [columns, setColumns] = useState(initialColumns);
    const [customFilters, setCustomFilters] = useState({
      companyId: "" + companyId,
      fyear: fYear_fetch,
      ...externalCustomFilters,
    });
    const [checked, setChecked] = useState(false);
    const [checkedExtended, setCheckedExtended] = useState(false);
    const [extendedTimes, setExtendedTimes] = useState(15);
    const [highlightRow, setHighlightRow] = useState([]);
    const [tableWidth, setTableWidth] = useState("1000px");
    const [selectedRows, setSelectedRows] = useState([]);
    const [allSelect, setAllSelect] = useState(false);

    const componentRef = useRef();

    // Print functionality
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    // Filter types
    const filterTypes = React.useMemo(
      () => ({
        text: (rows, id, filterValue) => {
          return rows.filter((row) => {
            const rowValue = row.values[id];
            return rowValue !== undefined
              ? String(rowValue)
                  .toLowerCase()
                  .startsWith(String(filterValue).toLowerCase())
              : true;
          });
        },
      }),
      []
    );

    const defaultColumn = React.useMemo(
      () => ({
        Filter: DefaultColumnFilter,
      }),
      []
    );

    // React Table setup
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      page,
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: { pageIndex, pageSize, sortBy },
    } = useTable(
      {
        columns,
        data,
        initialState: { pageIndex: 0, pageSize: 100 },
        defaultColumn,
        manualPagination: true,
        manualSortBy: true,
        autoResetPage: false,
        autoResetSortBy: false,
        pageCount: controlledPageCount,
      },
      useSortBy,
      usePagination
    );

    const formatSortFields = useCallback((sortBy) => {
      return sortBy.map((sort) => ({
        id: sort.id,
        desc: sort.desc,
      }));
    }, []);

    useEffect(() => {
      const formattedSortFields = formatSortFields(sortBy);
      fetchData({
        pageIndex,
        pageSize,
        sortBy: formattedSortFields,
        customFilters,
        dateObject,
      });
    }, [
      fetchData,
      pageIndex,
      pageSize,
      sortBy,
      customFilters,
      dateObject,
      formatSortFields,
    ]);

    // Drag and drop handler
    const onDragEnd = useCallback(
      (result) => {
        if (!result.destination) return;

        const newColumns = Array.from(columns);
        const [reorderedColumn] = newColumns.splice(result.source.index, 1);
        newColumns.splice(result.destination.index, 0, reorderedColumn);

        setColumns(newColumns);

        // Notify parent component of column reordering
        if (onColumnReorder) {
          onColumnReorder(newColumns);
        }
      },
      [columns, onColumnReorder]
    );

    useEffect(() => {
      setColumns(initialColumns);
    }, [initialColumns]);

    // Filter update handler
    const updateFilters = (e) => {
      let { name, value } = e.target;
      value =
        typeof value === "string"
          ? value.toLowerCase()
          : value.toString().toLowerCase();
      gotoPage(0);
      setCustomFilters({ ...customFilters, [name]: value });
    };

    // Transit filter handler
    const handleTransit = () => {
      setChecked(!checked);
      const newFilters = { ...customFilters };
      if (!checked) {
        newFilters.in_transit = 1;
      } else {
        newFilters.in_transit = null;
      }
      setCustomFilters(newFilters);
    };

    // Extended times filter handler
    const handleTransitExtendedTimes = () => {
      setCheckedExtended(!checkedExtended);
      const newFilters = { ...customFilters };
      if (!checkedExtended) {
        newFilters.greater_than_extended_times = extendedTimes;
      } else {
        delete newFilters.greater_than_extended_times;
      }
      setCustomFilters(newFilters);
    };

    // Row highlighting
    const toggleHighlightRow = (rowId) => {
      const hlRow = [...highlightRow];
      const idx = hlRow.indexOf(rowId);
      if (idx > -1) {
        hlRow.splice(idx, 1);
      } else {
        hlRow.push(rowId);
      }
      setHighlightRow(hlRow);
    };

    // Checkbox handlers
    const handleCheckbox = () => {
      if (allSelect) {
        setSelectedRows([]);
      } else {
        const allRowIds = data.map((row, index) => index);
        setSelectedRows(allRowIds);
      }
      setAllSelect(!allSelect);
    };

    const handleRowCheckBox = (rowIndex) => {
      const isSelected = selectedRows.includes(rowIndex);
      if (isSelected) {
        setSelectedRows(selectedRows.filter((id) => id !== rowIndex));
      } else {
        setSelectedRows([...selectedRows, rowIndex]);
      }
    };

    // Setup scrolling sync
    useEffect(() => {
      const wrapper1 = document.getElementById("wrapper1");
      const wrapper2 = document.getElementById("wrapper2");

      if (wrapper1 && wrapper2) {
        const syncScroll1 = () => {
          wrapper2.scrollLeft = wrapper1.scrollLeft;
        };
        const syncScroll2 = () => {
          wrapper1.scrollLeft = wrapper2.scrollLeft;
        };

        wrapper1.addEventListener("scroll", syncScroll1);
        wrapper2.addEventListener("scroll", syncScroll2);

        return () => {
          wrapper1.removeEventListener("scroll", syncScroll1);
          wrapper2.removeEventListener("scroll", syncScroll2);
        };
      }
    }, []);

    // Update table width
    useEffect(() => {
      if (componentRef.current) {
        setTableWidth(componentRef.current.offsetWidth + "px");
      }
    }, [columns, data]);

    return (
      <div className="report-container">
        {/* Transit and Extended Times Filters */}
        {(showTransitFilter || showExtendedTimesFilter) && (
          <div className="spaceBetw">
            {showTransitFilter && (
              <label>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={handleTransit}
                />
                In Transit ?
              </label>
            )}
            {showExtendedTimesFilter && (
              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={checkedExtended}
                    onChange={handleTransitExtendedTimes}
                  />
                  Extended Times&nbsp;&gt;&#61;&nbsp;
                </label>
                <input
                  size="1"
                  type="text"
                  value={extendedTimes}
                  onChange={(e) => setExtendedTimes(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const newFilters = { ...customFilters };
                      newFilters.greater_than_extended_times =
                        parseInt(extendedTimes);
                      setCustomFilters(newFilters);
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Synchronized scrolling headers */}
        <div id="wrapper1">
          <div className="div1" style={{ width: tableWidth }}></div>
        </div>

        {/* Main table container */}
        <div id="wrapper2">
          <DragDropContext onDragEnd={onDragEnd}>
            <table {...getTableProps()} className="div2" ref={componentRef}>
              <thead>
                <Droppable droppableId="table-headers" direction="horizontal">
                  {(provided) => (
                    <tr {...provided.droppableProps} ref={provided.innerRef}>
                      {/* Checkbox column */}
                      {checkbox && (
                        <th style={{ minWidth: "50px" }}>
                          <input
                            type="checkbox"
                            checked={allSelect}
                            onChange={handleCheckbox}
                          />
                        </th>
                      )}

                      {/* View/Action column */}
                      {(canView || customActions.length > 0) && (
                        <th style={{ minWidth: "100px" }}>Actions</th>
                      )}

                      {/* Draggable column headers */}
                      {headerGroups[0].headers.map((column, index) => (
                        <Draggable
                          key={column.id}
                          draggableId={column.id}
                          index={index}
                          isDragDisabled={column.disableDrag}
                        >
                          {(provided, snapshot) => (
                            <th
                              {...column.getHeaderProps(
                                column.getSortByToggleProps()
                              )}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={
                                index === 0 ? "sticky-col sticky-header" : ""
                              }
                              style={{
                                minWidth: column.minWidth || "150px",
                                width: column.width,
                                whiteSpace: "nowrap",
                                backgroundColor: snapshot.isDragging
                                  ? "#e3f2fd"
                                  : "#f5f5f5",
                                cursor: column.disableDrag ? "default" : "grab",
                                userSelect: "none",
                                ...provided.draggableProps.style,
                              }}
                            >
                              <div {...provided.dragHandleProps}>
                                <span style={{ fontSize: "16px" }}>
                                  {column.render("Header")}
                                  {column.isSorted
                                    ? column.isSortedDesc
                                      ? " 🔽"
                                      : " 🔼"
                                    : ""}
                                </span>
                              </div>

                              {/* Column filters */}
                              <div>
                                {column.id && column.canFilter && (
                                  <input
                                    name={column.id}
                                    value={customFilters[column.id] || ""}
                                    className="editable-input"
                                    onChange={updateFilters}
                                    placeholder="Search here..."
                                    style={{
                                      width: "100%",
                                      minWidth: column.minWidth || "150px",
                                      marginTop: "5px",
                                    }}
                                  />
                                )}
                                {column.id && column.canDropDown && (
                                  <CustomDropdown
                                    name={column.id}
                                    value={customFilters[column.id]}
                                    onChange={updateFilters}
                                    options={column.dropdownOptions}
                                  />
                                )}
                              </div>
                            </th>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tr>
                  )}
                </Droppable>
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, rowIndex) => {
                  prepareRow(row);
                  const currentRowId = row.cells[0]?.value;
                  const highlightCurrentRow =
                    highlightRow.indexOf(currentRowId) > -1;

                  return (
                    <tr
                      {...row.getRowProps()}
                      className="report-cell-tr"
                      style={{
                        backgroundColor: selectedRows.includes(rowIndex)
                          ? "#e8e8e8"
                          : rowIndex % 2 === 0
                          ? "#e4e4e4"
                          : "#faf0f0",
                      }}
                    >
                      {/* Checkbox column */}
                      {checkbox && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(rowIndex)}
                            onChange={() => handleRowCheckBox(rowIndex)}
                          />
                        </td>
                      )}

                      {/* Actions column */}
                      {(canView || customActions.length > 0) && (
                        <td>
                          <div style={{ display: "flex", gap: "5px" }}>
                            {canView && (
                              <button
                                style={{ width: "50px" }}
                                onClick={() => handleViewClick(row.cells)}
                              >
                                Open
                              </button>
                            )}
                            {customActions.map((action, actionIndex) => (
                              <button
                                key={actionIndex}
                                style={{ width: "60px", fontSize: "12px" }}
                                onClick={() => action.onClick(row)}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        </td>
                      )}

                      {/* Data columns */}
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          {...cell.getCellProps()}
                          className={cellIndex === 0 ? "sticky-col" : ""}
                          style={{
                            textDecoration: highlightCurrentRow
                              ? "underline"
                              : "",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            maxWidth: "0px",
                            overflow: "hidden",
                            // textTransform: "uppercase",
                            padding: "5px",
                            border: "1px solid #ddd",
                            backgroundColor: selectedRows.includes(rowIndex)
                              ? "#e8e8e8"
                              : rowIndex % 2 === 0
                              ? "#e4e4e4"
                              : "#faf0f0",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}

                      {/* Preview/Download/Delete actions */}
                      {canPreview && (
                        <>
                          <td>
                            <button
                              style={{ margin: "2px" }}
                              onClick={() => handlePreviewPod(row)}
                            >
                              Preview
                            </button>
                          </td>
                          <td>
                            <button
                              style={{ margin: "2px" }}
                              onClick={() => handleDownloadPod(row)}
                            >
                              Download
                            </button>
                          </td>
                          {(role_id === 1 || role_id === 2) && (
                            <td>
                              <button
                                style={{ margin: "2px" }}
                                onClick={() => handleDeletePod(row)}
                              >
                                Delete
                              </button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  );
                })}
                <tr>
                  {loading ? (
                    <td colSpan="100%" style={{ padding: "10px" }}>
                      Loading...
                    </td>
                  ) : (
                    <td colSpan="100%" style={{ padding: "10px" }}>
                      Showing {page.length} of ~{controlledPageCount * pageSize}{" "}
                      results
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </DragDropContext>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {"<<"}
          </button>
          <button onClick={() => previousPage()} disabled={!canPreviousPage}>
            {"<"}
          </button>
          <button onClick={() => nextPage()} disabled={!canNextPage}>
            {">"}
          </button>
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
          <span>
            Page{" "}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>
          </span>
          <span>
            | Go to page:{" "}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: "100px" }}
            />
          </span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <button onClick={handlePrint}>Print</button>
        </div>
      </div>
    );
  }
);

export default DraggableReportTable;
